import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Loader2, CheckCircle, ListChecks, Scale, Filter, Crosshair,
    Zap, Brain, Award, Sparkles, Clock, MapPin, RotateCcw, ChevronRight
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CH3_MODULES = [
    { id: 'inventario_frentes', name: 'Inventario de frentes', icon: ListChecks, color: '#3E5F6E' },
    { id: 'deber_prestado', name: 'Deber prestado', icon: Scale, color: '#E8B27A' },
    { id: 'filtro_impacto', name: 'Filtro de impacto', icon: Filter, color: '#7A9BA8' },
    { id: 'tarea_faro_3', name: 'Tarea faro', icon: Crosshair, color: '#5FAF9D' },
    { id: 'dai', name: 'Acción inevitable', icon: Zap, color: '#5FAF9D' }
];

export default function ResumenCapitulo3() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                setProfile(data);
            } catch {
                toast.error('Error al cargar datos');
                navigate('/usuario');
            } finally { setLoading(false); }
        };
        fetchProfile();
    }, [navigate]);

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    const completedModules = profile?.modules_completed || [];
    const ch3Completed = CH3_MODULES.filter(m => completedModules.some(cm => cm.module_id === m.id));
    const completionRate = (ch3Completed.length / CH3_MODULES.length) * 100;
    const aiSummaries = profile?.ai_summaries || [];
    const daiCard = profile?.dai_card;
    const lighthouseTask = profile?.lighthouse_task;

    const inventarioSummary = aiSummaries.find(s => s.module === 'inventario_frentes');
    const daiSummary = aiSummaries.find(s => s.module === 'dai');
    const filtroSummary = aiSummaries.find(s => s.module === 'filtro_impacto');

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <img src="/logo-senda.png" alt="SENDA" className="h-9 w-auto" />
                            <div>
                                <h1 className="text-xl font-semibold text-[#1F2A30] font-['Outfit']" data-testid="ch3-summary-title">Resumen Capítulo 3</h1>
                                <p className="text-sm text-[#4A5B63]">Foco, acción y compromiso</p>
                            </div>
                        </div>
                        <Link to="/usuario"><Button variant="ghost" className="text-[#3E5F6E] hover:bg-[#3E5F6E]/5">Volver al inicio</Button></Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#5FAF9D] via-[#4A9A89] to-[#3E5F6E] rounded-3xl p-8 lg:p-12 mb-8 shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="h-6 w-6 text-white" />
                                <span className="text-white/80 font-medium uppercase tracking-wide text-sm">Capítulo 3 Completado</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-light text-white font-['Outfit'] mb-4">Tu plan de acción está listo</h2>
                            <p className="text-white/80 text-lg max-w-xl">Has organizado tus frentes, filtrado el ruido y diseñado una acción con hora y lugar.</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="relative w-40 h-40">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="12" />
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="white" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${completionRate * 4.4} 440`} className="transition-all duration-1000" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-4xl font-bold text-white font-['Outfit']">{Math.round(completionRate)}%</span>
                                    <span className="text-white/60 text-sm">completado</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Module Progress */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><ListChecks className="h-5 w-5 text-[#3E5F6E]" />Progreso por módulo</CardTitle></CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {CH3_MODULES.map((module) => {
                                    const isCompleted = completedModules.some(cm => cm.module_id === module.id);
                                    const Icon = module.icon;
                                    return (
                                        <div key={module.id} className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCompleted ? 'bg-[#5FAF9D]' : 'bg-[#DCE3E8]'}`}>
                                                {isCompleted ? <CheckCircle className="h-5 w-5 text-white" /> : <Icon className="h-5 w-5 text-[#7A8A93]" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-sm font-medium ${isCompleted ? 'text-[#1F2A30]' : 'text-[#7A8A93]'}`}>{module.name}</span>
                                                    <span className={`text-xs ${isCompleted ? 'text-[#5FAF9D]' : 'text-[#7A8A93]'}`}>{isCompleted ? '100%' : '0%'}</span>
                                                </div>
                                                <div className="h-2 bg-[#DCE3E8] rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#5FAF9D] transition-all duration-500" style={{ width: isCompleted ? '100%' : '0%' }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Key Insights */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Sparkles className="h-5 w-5 text-[#5FAF9D]" />Hallazgos clave</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            {inventarioSummary?.nivel_sobrecarga && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#3E5F6E] uppercase mb-1">Nivel de sobrecarga</p>
                                    <p className="text-[#1F2A30] font-medium capitalize">{inventarioSummary.nivel_sobrecarga}</p>
                                </div>
                            )}
                            {filtroSummary?.porcentaje_pseudoavance !== undefined && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#E8B27A] uppercase mb-1">Pseudoavance detectado</p>
                                    <p className="text-[#1F2A30] font-medium">{filtroSummary.porcentaje_pseudoavance}%</p>
                                </div>
                            )}
                            {lighthouseTask && (
                                <div className="p-3 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-xl border border-[#5FAF9D]/20">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1 flex items-center gap-1"><Crosshair className="h-3 w-3" />Tarea faro</p>
                                    <p className="text-[#1F2A30] font-medium">{lighthouseTask}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* DAI Card */}
                {daiCard && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Zap className="h-5 w-5 text-[#5FAF9D]" />Tu Acción Inevitable (DAI)</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-3 bg-[#F5F7F8] rounded-lg">
                                    <div className="flex items-center gap-1 mb-1"><Zap className="h-3 w-3 text-[#E8B27A]" /><p className="text-xs text-[#7A8A93]">Acción</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{daiCard.primera_accion}</p>
                                </div>
                                <div className="p-3 bg-[#F5F7F8] rounded-lg">
                                    <div className="flex items-center gap-1 mb-1"><Clock className="h-3 w-3 text-[#3E5F6E]" /><p className="text-xs text-[#7A8A93]">Hora</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{daiCard.hora}</p>
                                </div>
                                <div className="p-3 bg-[#F5F7F8] rounded-lg">
                                    <div className="flex items-center gap-1 mb-1"><MapPin className="h-3 w-3 text-[#5FAF9D]" /><p className="text-xs text-[#7A8A93]">Lugar</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{daiCard.lugar}</p>
                                </div>
                                <div className="p-3 bg-[#F5F7F8] rounded-lg">
                                    <div className="flex items-center gap-1 mb-1"><RotateCcw className="h-3 w-3 text-[#7A9BA8]" /><p className="text-xs text-[#7A8A93]">Frecuencia</p></div>
                                    <p className="text-sm font-medium text-[#1F2A30]">{daiCard.frecuencia}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* AI Insight */}
                {daiSummary?.insight_usuario && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8">
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-[#5FAF9D]/20 rounded-full flex items-center justify-center shrink-0">
                                    <Brain className="h-5 w-5 text-[#5FAF9D]" />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Reflexión final</p>
                                    <p className="text-[#1F2A30] italic">"{daiSummary.insight_usuario}"</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Back to dashboard */}
                <div className="text-center">
                    <Link to="/usuario">
                        <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">
                            Volver al inicio <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
